/**
 * ACS means of transportation to work — public transit share and peer percentile
 * for the Climate Equity / Livability Index (Benefits → Mobility).
 *
 * Source: `means_of_transportation_to_work`, row
 * `Public transportation (excluding taxicab):` (aggregate mode share).
 * Higher transit share → higher benefit percentile among California peers
 * of the same geography type (city vs city, county vs county) in that year.
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export const PUBLIC_TRANSIT_MODE_LABEL = 'Public transportation (excluding taxicab):';

export type PublicTransitLivabilityResult = {
  percentile: number;
  percent: number;
  year: number;
};

type MotRow = {
  year?: number | null;
  percent?: number | string | null;
  percentile?: number | string | null;
  location_name?: string | null;
  geography_type?: string | null;
  geofips?: string | number | null;
};

function toDisplayPercentile(raw: unknown): number | null {
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  const n = Number(raw);
  return Math.round(n <= 1 ? n * 100 : n);
}

function toPercent(raw: unknown): number | null {
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Number(raw);
}

function pickLatestRow(rows: MotRow[]): MotRow | null {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => Number(b.year ?? 0) - Number(a.year ?? 0))[0];
}

async function fetchMotRows(
  geographyType: 'place' | 'county',
  location: string,
  geofips?: number | string | null
): Promise<MotRow[]> {
  let query = supabaseClient
    .from('means_of_transportation_to_work')
    .select('year, percent, percentile, location_name, geography_type, geofips')
    .eq('geography_type', geographyType)
    .eq('means_of_transportation', PUBLIC_TRANSIT_MODE_LABEL)
    .not('percentile', 'is', null)
    .order('year', { ascending: false });

  if (geographyType === 'place') {
    query = query.ilike('location_name', `%${location.trim()}%`);
  } else if (geofips != null) {
    query = query.eq('geofips', geofips);
  } else {
    query = query.ilike('location_name', `%${location.trim()}%`);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error loading public transit percentile:', error);
    return [];
  }
  return (data ?? []) as MotRow[];
}

async function fetchFromLivabilityPrecalculated(
  location: string,
  year?: number
): Promise<PublicTransitLivabilityResult | null> {
  const loc = location.trim();
  const locType = supabaseLocationService.getLocationType(location);
  const tryTypes: Array<'City' | 'County' | 'Place'> =
    locType === 'county' ? ['County'] : ['City', 'Place'];

  for (const gt of tryTypes) {
    let q = supabaseClient
      .from('livability_index_precalculated')
      .select('public_transit_percentile, public_transit_year, year')
      .eq('location_name', loc)
      .eq('geography_type', gt)
      .not('public_transit_percentile', 'is', null)
      .order('year', { ascending: false })
      .limit(1);
    if (year != null) q = q.eq('year', year);
    const { data, error } = await q;
    if (error) continue;
    const row = data?.[0];
    const pct = toDisplayPercentile(row?.public_transit_percentile);
    if (pct != null) {
      const y = Number(row?.public_transit_year ?? row?.year);
      return { percentile: pct, percent: NaN, year: Number.isFinite(y) ? y : 2023 };
    }
  }

  if (locType !== 'county') {
    const { data } = await supabaseClient
      .from('livability_index_precalculated')
      .select('public_transit_percentile, public_transit_year, year')
      .ilike('location_name', `${loc}%`)
      .eq('geography_type', 'City')
      .not('public_transit_percentile', 'is', null)
      .order('year', { ascending: false })
      .limit(1);
    const row = data?.[0];
    const pct = toDisplayPercentile(row?.public_transit_percentile);
    if (pct != null) {
      const y = Number(row?.public_transit_year ?? row?.year);
      return { percentile: pct, percent: NaN, year: Number.isFinite(y) ? y : 2023 };
    }
  }

  return null;
}

/**
 * Latest-year public transit benefit percentile for a place or county.
 * Prefers `livability_index_precalculated.public_transit_percentile` when populated.
 */
export async function fetchPublicTransitLivabilityPercentile(
  location: string,
  geofips?: number | string | null,
  options?: { year?: number; skipPrecalculated?: boolean }
): Promise<PublicTransitLivabilityResult | null> {
  if (!location?.trim()) return null;

  if (!options?.skipPrecalculated) {
    const precalc = await fetchFromLivabilityPrecalculated(location, options?.year);
    if (precalc) {
      if (!Number.isFinite(precalc.percent)) {
        const mot = await fetchMotRows(
          supabaseLocationService.getLocationType(location) === 'county' ? 'county' : 'place',
          location,
          geofips
        );
        const latest = pickLatestRow(mot);
        const percent = latest ? toPercent(latest.percent) : null;
        if (percent != null) return { ...precalc, percent };
      }
      return precalc;
    }
  }

  const locType = supabaseLocationService.getLocationType(location);
  const primaryGeo: 'place' | 'county' = locType === 'county' ? 'county' : 'place';
  let rows = await fetchMotRows(primaryGeo, location, geofips);

  if (!rows.length && primaryGeo === 'place') {
    rows = await fetchMotRows('county', location, geofips);
  }

  if (options?.year != null) {
    rows = rows.filter((r) => Number(r.year) === options.year);
  }

  const latest = pickLatestRow(rows);
  if (!latest) return null;

  const percentile = toDisplayPercentile(latest.percentile);
  const percent = toPercent(latest.percent);
  const year = Number(latest.year);
  if (percentile == null || percent == null || !Number.isFinite(year)) return null;

  return { percentile, percent, year };
}
